import * as React from 'react';
import {KeyboardEventHandler, ReactNode, useState} from 'react';
import {CheckboxInput} from '~/components/Controls/Checkbox';
import {IInputFieldProps} from '~/components/Form';
import {classByIntent} from '~/lib/intent';
import style from './index.module.scss';

export enum InputType {
    PASSWORD,
    EMAIL,
    STRING,
    FLOAT,
    INTEGER
}

const nativeInputTypes: { [key: number]: string } = {
    [InputType.PASSWORD]: 'password',
    [InputType.EMAIL]: 'email',
    [InputType.FLOAT]: 'number',
    [InputType.INTEGER]: 'number',
    [InputType.STRING]: 'text'
};

export interface IInputProps extends IInputFieldProps<any> {
    onOmitChange?: (isOmitted: boolean) => any;
    rightIcon?: ReactNode;
    leftIcon?: ReactNode;
    placeholder?: string;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    className?: string;
    autoFocus?: boolean;
    required?: boolean;
    type?: InputType;
    passwordToggle?: boolean;
    small?: boolean;
    large?: boolean;
    step?: any;
    max?: any;
    min?: any;
}

export const Input = React.forwardRef<HTMLInputElement, IInputProps>(({
    onOmitChange,
    disabled,
    required,
    intent,
    onKeyDown,
    leftIcon,
    rightIcon,
    onChange,
    value,
    placeholder,
    id,
    type = InputType.STRING,
    passwordToggle,
    className,
    autoFocus,
    ...otherProps
}, inputRef) => {
    let classes = [style.input, className, classByIntent(intent)];
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const passwordToggleEnabled = Boolean(passwordToggle && type === InputType.PASSWORD);
    const inputType = passwordToggleEnabled && isPasswordVisible ? InputType.STRING : type;
    const resolvedRightIcon = passwordToggleEnabled ? (
        <i
            className={isPasswordVisible ? 'fa fa-eye-slash' : 'fa fa-eye'}
            onClick={() => setPasswordVisible(v => !v)}
            style={{cursor: 'pointer'}}
        />
    ) : rightIcon;

    if (leftIcon) {
        classes.push(style.inputIconLeft);
    }
    if (resolvedRightIcon) {
        classes.push(style.inputIconRight);
    }

    const [isOmit, setOmit] = useState(!!value);

    const onChangeWithCast = (newValue: string) => {
        /*
        if (newValue) {
            try {
                console.log('t1 ', newValue);
                console.log(typeof newValue);
                switch (type) {
                    case InputType.FLOAT:
                        onChange(Number.parseFloat(newValue));
                        break;
                    case InputType.INTEGER:
                        onChange(Number.parseInt(newValue, 10));
                        break;
                    default:
                        onChange(newValue);
                }
            } finally {
                onChange(newValue);
            }
        } else {
            onChange(newValue);
        }
        */
        onChange?.(newValue);
    };

    return <div className={classes.join(' ')}>
        {onOmitChange && <CheckboxInput disabled={disabled} className={style.omit} value={isOmit} onChange={newOmit => {
            setOmit(newOmit);
            onChangeWithCast(onOmitChange(!newOmit));
        }}/>}
        {leftIcon}
        <input {...otherProps} value={[InputType.FLOAT, InputType.INTEGER].includes(type) && value !== undefined ? value : (value || '')}
            type={nativeInputTypes[inputType]}
            autoFocus={autoFocus}
            onChange={event => onChangeWithCast(event.target.value)}
            disabled={disabled || (!isOmit && !!onOmitChange)}
            required={required}
            placeholder={placeholder}
            id={id}
            onKeyDown={event => {
                if ([InputType.FLOAT, InputType.INTEGER].includes(type) && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
                    event.preventDefault();
                }
                onKeyDown?.(event);
            }}
            ref={inputRef || undefined}
        />
        {resolvedRightIcon}
    </div>;
});
